import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FileText,
  Upload,
  Loader2,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Link as LinkIcon,
  CheckCircle2,
  AlertCircle,
  Download,
} from "lucide-react";
import {
  fetchAdminDocuments,
  uploadAdminDocument,
  updateAdminDocument,
  deleteAdminDocument,
  documentDownloadUrl,
} from "@/api/documents";

const LEDEN_URL = "https://lemmer-leeft.nl/leden";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("nl-NL", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

// ---- Upload-formulier ----------------------------------------------------
const UploadForm = ({ onDone, showToast }) => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [docDate, setDocDate] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      showToast("Kies een bestand (PDF of office-document)", "error");
      return;
    }
    if (!title.trim()) {
      showToast("Vul een titel in", "error");
      return;
    }
    setSaving(true);
    try {
      await uploadAdminDocument({ file, title: title.trim(), description, doc_date: docDate });
      showToast(`"${title.trim()}" geüpload als concept — publiceer het als het klaar is`);
      setFile(null);
      setTitle("");
      setDescription("");
      setDocDate("");
      if (fileRef.current) fileRef.current.value = "";
      onDone();
    } catch (err) {
      showToast(err?.response?.data?.detail || "Upload mislukt", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="document-upload-form"
      className="bg-white rounded-2xl border border-[#E4EEF5] p-5 mb-6"
    >
      <h2 className="font-[Poppins] font-bold text-[#173A5E] text-lg mb-4">
        Nieuw document uploaden
      </h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="block text-sm font-[Poppins] font-semibold text-[#173A5E] mb-1">
            Bestand * <span className="font-normal text-[#5A7594]">(PDF of office-type, max 25 MB)</span>
          </label>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.csv,.txt"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="document-file-input"
            className="w-full text-sm text-[#3E5871] file:mr-3 file:px-4 file:py-2 file:rounded-full file:border-0 file:bg-[#173A5E] file:text-white file:font-[Poppins] file:font-semibold file:text-sm file:cursor-pointer hover:file:bg-[#0F2A46]"
          />
        </div>
        <div>
          <label className="block text-sm font-[Poppins] font-semibold text-[#173A5E] mb-1">
            Titel *
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="document-title-input"
            className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2 focus:border-[#6BC0E8] outline-none"
            placeholder="Bijv. Notulen ALV 2026"
          />
        </div>
        <div>
          <label className="block text-sm font-[Poppins] font-semibold text-[#173A5E] mb-1">
            Datum
          </label>
          <input
            type="date"
            value={docDate}
            onChange={(e) => setDocDate(e.target.value)}
            data-testid="document-date-input"
            className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2 focus:border-[#6BC0E8] outline-none"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm font-[Poppins] font-semibold text-[#173A5E] mb-1">
            Omschrijving
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            data-testid="document-description-input"
            className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2 focus:border-[#6BC0E8] outline-none"
            placeholder="Korte toelichting voor leden"
          />
        </div>
      </div>
      <div className="flex justify-end mt-4">
        <button
          type="submit"
          disabled={saving}
          data-testid="document-upload-submit"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#173A5E] hover:bg-[#0F2A46] text-white font-[Poppins] font-semibold text-sm disabled:opacity-60 transition"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Uploaden
        </button>
      </div>
    </form>
  );
};

// ---- Bewerk-modal --------------------------------------------------------
const EditModal = ({ doc, onClose, onDone, showToast }) => {
  const [title, setTitle] = useState(doc.title);
  const [description, setDescription] = useState(doc.description || "");
  const [docDate, setDocDate] = useState(doc.doc_date || "");
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateAdminDocument(doc.id, {
        title: title.trim(),
        description,
        doc_date: docDate,
      });
      showToast("Document bijgewerkt");
      onDone();
    } catch (err) {
      showToast(err?.response?.data?.detail || "Bijwerken mislukt", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="document-edit-modal"
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <form onSubmit={onSubmit} className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold text-[#173A5E] font-[Poppins] mb-4">Document bewerken</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-[Poppins] font-semibold text-[#173A5E] mb-1">Titel *</label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="document-edit-title"
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2 focus:border-[#6BC0E8] outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-[Poppins] font-semibold text-[#173A5E] mb-1">Datum</label>
            <input
              type="date"
              value={docDate}
              onChange={(e) => setDocDate(e.target.value)}
              data-testid="document-edit-date"
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2 focus:border-[#6BC0E8] outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-[Poppins] font-semibold text-[#173A5E] mb-1">Omschrijving</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              data-testid="document-edit-description"
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2 focus:border-[#6BC0E8] outline-none"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-[#173A5E] hover:bg-[#F4F8FB] font-[Poppins] font-semibold text-sm"
          >
            Annuleren
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="document-edit-save"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#173A5E] hover:bg-[#0F2A46] text-white font-[Poppins] font-semibold text-sm disabled:opacity-60"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Opslaan
          </button>
        </div>
      </form>
    </div>
  );
};

// ---- Hoofdpagina ----------------------------------------------------------
export const AdminDocumentsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAdminDocuments();
      setItems(data.items || []);
    } catch (e) {
      showToast(e?.response?.data?.detail || "Ophalen mislukt", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const togglePublish = async (doc) => {
    try {
      await updateAdminDocument(doc.id, { published: !doc.published });
      showToast(doc.published ? `"${doc.title}" gedepubliceerd` : `"${doc.title}" gepubliceerd op /leden`);
      load();
    } catch (err) {
      showToast(err?.response?.data?.detail || "Publiceren mislukt", "error");
    }
  };

  const doDelete = async () => {
    const doc = confirmDelete;
    setConfirmDelete(null);
    try {
      await deleteAdminDocument(doc.id);
      showToast(`"${doc.title}" verwijderd`);
      load();
    } catch (err) {
      showToast(err?.response?.data?.detail || "Verwijderen mislukt", "error");
    }
  };

  const publishedCount = items.filter((d) => d.published).length;

  return (
    <div data-testid="admin-documents-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#173A5E] font-[Poppins]">
          Documenten
        </h1>
        <p className="text-[#5A7594] font-[Inter] mt-1 text-sm">
          {items.length} document{items.length === 1 ? "" : "en"} · {publishedCount} gepubliceerd
        </p>
      </div>

      {/* Vaste deel-link */}
      <div className="bg-[#EAF6FD] border border-[#BEE3F5] rounded-2xl p-4 mb-6 flex items-start gap-3">
        <LinkIcon className="w-5 h-5 text-[#2E9CD6] shrink-0 mt-0.5" />
        <div className="text-sm text-[#173A5E] font-[Inter]">
          <p className="font-[Poppins] font-semibold mb-0.5">De vaste link om te delen:</p>
          <code data-testid="leden-share-link" className="text-[#2E9CD6] font-semibold">{LEDEN_URL}</code>
          <p className="text-[#5A7594] mt-1">
            Deze pagina is niet vindbaar via zoekmachines of het menu — alleen via deze directe link.
          </p>
        </div>
      </div>

      <UploadForm onDone={load} showToast={showToast} />

      {/* Lijst */}
      <div className="bg-white rounded-2xl border border-[#E4EEF5] overflow-hidden">
        {loading ? (
          <div className="flex items-center gap-2 text-[#5A7594] p-6">
            <Loader2 className="w-5 h-5 animate-spin" /> Laden…
          </div>
        ) : items.length === 0 ? (
          <p data-testid="documents-empty" className="text-[#5A7594] p-6">
            Nog geen documenten. Upload hierboven het eerste document.
          </p>
        ) : (
          <ul data-testid="documents-list" className="divide-y divide-[#E4EEF5]">
            {items.map((doc) => (
              <li
                key={doc.id}
                data-testid={`document-row-${doc.id}`}
                className="flex flex-wrap items-center gap-3 px-5 py-4"
              >
                <span className="shrink-0 w-10 h-10 rounded-xl bg-[#F4F8FB] flex items-center justify-center text-[#2E9CD6]">
                  <FileText className="w-5 h-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-[Poppins] font-semibold text-[#173A5E] truncate">{doc.title}</p>
                  <p className="text-xs text-[#5A7594]">
                    {formatDate(doc.doc_date)} · {doc.filename}
                  </p>
                </div>
                <span
                  data-testid={`document-status-${doc.id}`}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-[Poppins] font-semibold ${
                    doc.published
                      ? "bg-[#E6F5EA] text-[#2A7A3F]"
                      : "bg-[#F4F8FB] text-[#5A7594]"
                  }`}
                >
                  {doc.published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  {doc.published ? "Gepubliceerd" : "Concept"}
                </span>
                <div className="flex items-center gap-1">
                  <a
                    href={documentDownloadUrl(doc.id)}
                    title="Download"
                    data-testid={`document-download-${doc.id}`}
                    className="p-2 rounded-lg text-[#173A5E] hover:bg-[#F4F8FB]"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                  <button
                    type="button"
                    onClick={() => togglePublish(doc)}
                    title={doc.published ? "Depubliceren" : "Publiceren"}
                    data-testid={`document-toggle-${doc.id}`}
                    className={`p-2 rounded-lg ${
                      doc.published
                        ? "text-[#2A7A3F] hover:bg-[#E6F5EA]"
                        : "text-[#5A7594] hover:bg-[#F4F8FB]"
                    }`}
                  >
                    {doc.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(doc)}
                    title="Bewerken"
                    data-testid={`document-edit-${doc.id}`}
                    className="p-2 rounded-lg text-[#173A5E] hover:bg-[#F4F8FB]"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(doc)}
                    title="Verwijderen"
                    data-testid={`document-delete-${doc.id}`}
                    className="p-2 rounded-lg text-[#8b1420] hover:bg-[#FDECEE]"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <EditModal
          doc={editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            load();
          }}
          showToast={showToast}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div data-testid="document-delete-modal" className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-[#173A5E] font-[Poppins] mb-2">Document verwijderen?</h3>
            <p className="text-sm text-[#5A7594] mb-4">
              Weet je zeker dat je <strong>{confirmDelete.title}</strong> wilt verwijderen? Het bestand
              wordt ook definitief verwijderd.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-lg text-[#173A5E] hover:bg-[#F4F8FB] font-[Poppins] font-semibold text-sm"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={doDelete}
                data-testid="document-delete-confirm"
                className="px-4 py-2 rounded-lg bg-[#8b1420] hover:bg-[#6d0f19] text-white font-[Poppins] font-semibold text-sm"
              >
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div
          data-testid="admin-documents-toast"
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-[Poppins] font-medium ${
            toast.type === "error" ? "bg-[#8b1420] text-white" : "bg-[#173A5E] text-white"
          }`}
        >
          {toast.type === "error" ? <AlertCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default AdminDocumentsPage;
